import React, { useState, useEffect, useCallback } from 'react';
import { lifetimeRewardsAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';

const AdminLifetimeRewards = () => {
  const [qualifiedUsers, setQualifiedUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRewards, setUserRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [activeViewTab, setActiveViewTab] = useState('ALL'); // 'ALL' | 'PENDING' | 'COMPLETED'
  const [userStats, setUserStats] = useState(null);

  // Rank configurations based on matching business - SEQUENTIAL ORDER
  const sequentialRewardOrder = ['STAR', 'SILVER_STAR', 'GOLD', 'PLATINUM', 'DIAMOND', 
    'BLUE_DIAMOND', 'ROYAL_DIAMOND', 'CROWN_DIAMOND', 'CROWN_AMBASSADOR', 
    'DOUBLE_CROWN_STAR', 'DIRECTOR_STAR', 'DIRECTOR_CLUB'];
  
  const rankConfigs = {
    'STAR': { matchingRequired: 100000, reward: 5000, color: 'bg-green-500' },
    'SILVER_STAR': { matchingRequired: 250000, reward: 12500, color: 'bg-gray-400' },
    'GOLD': { matchingRequired: 500000, reward: 25000, color: 'bg-yellow-500' },
    'PLATINUM': { matchingRequired: 1000000, reward: 50000, color: 'bg-purple-500' },
    'DIAMOND': { matchingRequired: 2500000, reward: 125000, color: 'bg-blue-500' },
    'BLUE_DIAMOND': { matchingRequired: 5000000, reward: 250000, color: 'bg-cyan-500' },
    'ROYAL_DIAMOND': { matchingRequired: 10000000, reward: 500000, color: 'bg-indigo-500' },
    'CROWN_DIAMOND': { matchingRequired: 20000000, reward: 1000000, color: 'bg-orange-500' },
    'CROWN_AMBASSADOR': { matchingRequired: 50000000, reward: 2500000, color: 'bg-red-500' },
    'DOUBLE_CROWN_STAR': { matchingRequired: 150000000, reward: 7500000, color: 'bg-pink-500' },
    'DIRECTOR_STAR': { matchingRequired: 1000000000, reward: 50000000, color: 'bg-violet-600' },
    'DIRECTOR_CLUB': { matchingRequired: 5000000000, reward: 250000000, color: 'bg-black' }
  };

  const fetchQualifiedUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await lifetimeRewardsAPI.getAllUsers();
      if (response.data.success) {
        setQualifiedUsers(response.data.data);
        setUserStats(response.data.stats);
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch qualified users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await lifetimeRewardsAPI.getSummary();
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  useEffect(() => {
    fetchQualifiedUsers();
    fetchSummary();
  }, [fetchQualifiedUsers]);

  const handleCalculateBinaryTree = async (userId) => {
    try {
      setLoading(true);
      const response = await lifetimeRewardsAPI.calculateBinaryTree(userId);
      if (response.data.success) {
        // Refresh the qualified users to show updated data
        await fetchQualifiedUsers();

        // Show rank achievement notification
        if (response.data.data.rankAchievement) {
          const { newRank, rewardAmount } = response.data.data.rankAchievement;
          alert(`Congratulations! User achieved ${newRank} rank with reward of ${formatCurrency(rewardAmount)}`);
        }
      }
    } catch (err) {
      setError('Failed to calculate lifetime legs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserRewards = async (user) => {
    try {
      setLoading(true);
      const response = await lifetimeRewardsAPI.getUserRewards(user._id);
      if (response.data.success) {
        setUserRewards(response.data.data);
        setSelectedUser(user);
        setShowUserModal(true);
      }
    } catch (err) {
      setError('Failed to fetch user rewards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDistributeReward = async (userId, rank) => {
    try {
      setLoading(true);
      
      // If userRewards is not loaded (e.g., called from table), fetch it first
      if (!userRewards || userRewards._id !== userId) {
        try {
          const response = await lifetimeRewardsAPI.getUserRewards(userId);
          if (response.data.success) {
            setUserRewards(response.data.data);
          }
        } catch (err) {
          console.error('Error fetching user rewards:', err);
          // Continue without userRewards, backend will validate
        }
      }
      
      // Check if this is the current sequential reward (if we have userRewards)
      if (userRewards && userRewards.sequentialRewards) {
        const currentIndex = userRewards.sequentialRewards.currentRewardIndex || 0;
        const currentRewardRank = sequentialRewardOrder[currentIndex];
        
        if (rank !== currentRewardRank) {
          alert(`Sequential order violation: You must distribute ${currentRewardRank.replace('_', ' ')} reward first before ${rank.replace('_', ' ')}.`);
          return;
        }
      }

      // First, calculate the binary tree to ensure achievement is created if user qualifies
      await lifetimeRewardsAPI.calculateBinaryTree(userId);

      // Then distribute the reward
      const response = await lifetimeRewardsAPI.distributeReward({
        userId,
        rank,
        notes: `Lifetime reward distribution for ${rank}`
      });

      if (response.data.success) {
        const carryMsg = response.data.data.carriedForwardBalance > 0
          ? ` (${formatCurrency(response.data.data.carriedForwardBalance)} carried forward toward next reward)`
          : '';
        alert(`Reward of ${formatCurrency(response.data.data.rewardAmount)} sent successfully! Credited to user's Lifetime Rewards wallet.${carryMsg}`);

        // Refresh user rewards and qualified users
        if (selectedUser) {
          await handleViewUserRewards(selectedUser);
        }
        await fetchQualifiedUsers();
        await fetchSummary();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to distribute reward';
      setError(errorMsg);
      alert(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupIncorrectAchievements = async () => {
    if (!window.confirm('This will clean up all incorrect lifetime reward achievements that don\'t match current qualification based on NEW matching income. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await lifetimeRewardsAPI.cleanupIncorrectAchievements();
      
      if (response.data.success) {
        alert(response.data.message);
        await fetchQualifiedUsers();
        await fetchSummary();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to cleanup achievements';
      setError(errorMsg);
      alert(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDistributeRewardFromTable = async (userId, rank) => {
    try {
      setLoading(true);
      
      // First, calculate the binary tree to ensure achievement is created if user qualifies
      await lifetimeRewardsAPI.calculateBinaryTree(userId);

      // Then distribute the reward
      const response = await lifetimeRewardsAPI.distributeReward({
        userId,
        rank,
        notes: `Lifetime reward distribution for ${rank}`
      });

      if (response.data.success) {
        const carryMsg = response.data.data.carriedForwardBalance > 0
          ? ` (${formatCurrency(response.data.data.carriedForwardBalance)} carried forward toward next reward)`
          : '';
        alert(`Reward of ${formatCurrency(response.data.data.rewardAmount)} sent successfully! Credited to user's Lifetime Rewards wallet.${carryMsg}`);

        // Refresh qualified users
        await fetchQualifiedUsers();
        await fetchSummary();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to distribute reward';
      setError(errorMsg);
      alert(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getRankColor = (rank) => {
    return rankConfigs[rank]?.color || 'bg-gray-500';
  };

  // Extract all distributed / paid rewards across all users
  const completedRewardsList = qualifiedUsers.flatMap(user => {
    const list = (user.rankAchievements || []).filter(a => a.rewardDistributed);
    return list.map(ach => ({
      _id: `${user._id}-${ach.rank}`,
      userId: user._id,
      userName: user.name,
      userEmail: user.email,
      userPhone: user.phone,
      mySponsorCode: user.mySponsorCode,
      rank: ach.rank,
      rewardAmount: ach.rewardAmount,
      matchingBusiness: ach.matchingBusiness || user.legStructure?.matchingBusiness || 0,
      businessUsed: ach.businessUsed || 0,
      achievedAt: ach.achievedAt,
      distributedAt: ach.rewardDistributedAt || ach.achievedAt,
      user
    }));
  }).sort((a, b) => new Date(b.distributedAt || 0) - new Date(a.distributedAt || 0));

  const filteredCompletedRewards = completedRewardsList.filter(item => {
    const matchesSearch = !searchTerm ||
      (item.userName && item.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.userEmail && item.userEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.userPhone && item.userPhone.includes(searchTerm)) ||
      (item.mySponsorCode && item.mySponsorCode.includes(searchTerm));

    const matchesRank = !rankFilter || item.rank === rankFilter;
    return matchesSearch && matchesRank;
  });

  // Filter users - Enhanced with status filter and tab sync
  const filteredUsers = qualifiedUsers.filter(user => {
    const matchesSearch = !searchTerm ||
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.phone && user.phone.includes(searchTerm)) ||
      (user.mySponsorCode && user.mySponsorCode.includes(searchTerm));

    const matchesRank = !rankFilter ||
      (user.legStructure && user.legStructure.currentRank === rankFilter);

    const matchesStatus = statusFilter === 'ALL' ||
      (statusFilter === 'COMPLETED'
        ? (user.status === 'COMPLETED' || user.completed || user.hasCompletedRewards || (user.totalRewardsDistributed > 0))
        : statusFilter === 'PENDING_REWARDS'
        ? user.hasPendingRewards
        : statusFilter === 'QUALIFIED'
        ? user.qualifiesForCurrentReward
        : statusFilter === 'NOT_QUALIFIED'
        ? (!user.qualifiesForCurrentReward && !user.hasPendingRewards && !user.completed && !user.hasCompletedRewards)
        : user.status === statusFilter);

    const matchesTab = activeViewTab === 'ALL' ||
      (activeViewTab === 'PENDING' && user.hasPendingRewards) ||
      (activeViewTab === 'COMPLETED' && (user.completed || user.hasCompletedRewards || (user.totalRewardsDistributed > 0)));

    return matchesSearch && matchesRank && matchesStatus && matchesTab;
  });

  if (loading && qualifiedUsers.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Lifetime Rewards</h1>
        <p className="text-gray-600">
          Comprehensive view of users qualified for lifetime rewards and those who have completed their lifetime reward journey.
          Volumes use each person&apos;s total business (self + downline). Power leg = direct with highest total
          business. Rest of legs = all other directs&apos; total business combined.
        </p>
      </div>

      {/* Summary Cards */}
      {userStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div
            onClick={() => {
              setActiveViewTab('ALL');
              setStatusFilter('ALL');
            }}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-all border-2 ${
              activeViewTab === 'ALL' && statusFilter === 'ALL' ? 'border-blue-500 bg-blue-50/20' : 'border-transparent'
            }`}
          >
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Users in System</h3>
            <p className="text-2xl font-bold text-blue-600">{userStats.totalUsers || qualifiedUsers.length}</p>
            <p className="text-xs text-gray-500 mt-1">Qualified + Completed users</p>
          </div>
          <div
            onClick={() => {
              setActiveViewTab('COMPLETED');
              setStatusFilter('COMPLETED');
            }}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-all border-2 ${
              activeViewTab === 'COMPLETED' ? 'border-green-500 bg-green-50/20' : 'border-transparent'
            }`}
          >
            <h3 className="text-sm font-medium text-gray-500 mb-2">Completed Rewards</h3>
            <p className="text-2xl font-bold text-green-600">
              {userStats.totalCompletedRewards ?? completedRewardsList.length}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {userStats.completedUsers || qualifiedUsers.filter(u => u.completed || u.hasCompletedRewards).length} users received rewards (Click to view)
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Qualified for Current Reward</h3>
            <p className="text-2xl font-bold text-purple-600">{userStats.qualifiedUsers || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Meets current requirement</p>
          </div>
          <div
            onClick={() => {
              setActiveViewTab('PENDING');
              setStatusFilter('PENDING_REWARDS');
            }}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-all border-2 ${
              activeViewTab === 'PENDING' ? 'border-orange-500 bg-orange-50/20' : 'border-transparent'
            }`}
          >
            <h3 className="text-sm font-medium text-gray-500 mb-2">Pending Rewards</h3>
            <p className="text-2xl font-bold text-orange-600">{userStats.pendingRewardsUsers || qualifiedUsers.filter(u => u.hasPendingRewards).length}</p>
            <p className="text-xs text-gray-500 mt-1">Awaiting distribution (Click to view)</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Not Qualified Yet</h3>
            <p className="text-2xl font-bold text-gray-600">{userStats.notQualifiedUsers || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Need more business</p>
          </div>
        </div>
      )}

      {/* Legacy Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Power Leg Business</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.summary?.totalPowerBusiness || 0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Support Leg Business</h3>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(summary.summary?.totalSupportBusiness || 0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Matching Business</h3>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.summary?.totalMatchingBusiness || 0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Rewards Distributed</h3>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.totalRewardsDistributed?.totalAmount || 0)}</p>
          </div>
          {summary.pendingRewardsStats && (
            <>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Pending Rewards</h3>
                <p className="text-2xl font-bold text-amber-600">{summary.pendingRewardsStats?.totalPendingRewards || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Awaiting distribution</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Pending Amount</h3>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary.pendingRewardsStats?.totalPendingAmount || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">To be distributed</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or phone"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Rank</label>
            <select
              value={rankFilter}
              onChange={(e) => setRankFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Ranks</option>
              {Object.keys(rankConfigs).map(rank => (
                <option key={rank} value={rank}>{rank.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                const val = e.target.value;
                setStatusFilter(val);
                if (val === 'COMPLETED') {
                  setActiveViewTab('COMPLETED');
                } else if (val === 'PENDING_REWARDS') {
                  setActiveViewTab('PENDING');
                } else {
                  setActiveViewTab('ALL');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="COMPLETED">Completed Rewards (Paid)</option>
              <option value="PENDING_REWARDS">Pending Rewards (Awaiting Distribution)</option>
              <option value="QUALIFIED">Qualified for Current Reward</option>
              <option value="NOT_QUALIFIED">Not Qualified Yet</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={fetchQualifiedUsers}
              className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
            >
              Refresh Data
            </button>
            <button
              onClick={handleCleanupIncorrectAchievements}
              className="flex-1 bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
              title="Clean up incorrect achievements that don't match current qualification"
            >
              Cleanup Achievements
            </button>
          </div>
        </div>
      </div>

      {/* Section View Tabs */}
      <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-lg px-4 pt-3 shadow-sm flex-wrap gap-2">
        <button
          onClick={() => {
            setActiveViewTab('ALL');
            if (statusFilter === 'COMPLETED' || statusFilter === 'PENDING_REWARDS') {
              setStatusFilter('ALL');
            }
          }}
          className={`px-5 py-2.5 font-semibold text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeViewTab === 'ALL'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span>👥 All Users</span>
          <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full font-bold">
            {qualifiedUsers.length}
          </span>
        </button>

        <button
          onClick={() => {
            setActiveViewTab('PENDING');
            setStatusFilter('PENDING_REWARDS');
          }}
          className={`px-5 py-2.5 font-semibold text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeViewTab === 'PENDING'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span>⏳ Pending Rewards</span>
          <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-bold">
            {userStats?.pendingRewardsUsers || qualifiedUsers.filter(u => u.hasPendingRewards).length}
          </span>
        </button>

        <button
          onClick={() => {
            setActiveViewTab('COMPLETED');
            setStatusFilter('COMPLETED');
          }}
          className={`px-5 py-2.5 font-semibold text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeViewTab === 'COMPLETED'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span>✅ Completed Section</span>
          <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold">
            {completedRewardsList.length}
          </span>
        </button>
      </div>

      {/* DEDICATED COMPLETED SECTION */}
      {activeViewTab === 'COMPLETED' ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 bg-green-50/50 flex justify-between items-center flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold text-green-900 flex items-center gap-2">
                <span>🏆 Completed & Distributed Lifetime Rewards</span>
                <span className="text-xs bg-green-200 text-green-800 font-semibold px-2.5 py-0.5 rounded-full">
                  {filteredCompletedRewards.length} Completed
                </span>
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                All lifetime rewards that have been successfully distributed, approved, and credited to members' Lifetime Rewards wallets.
              </p>
            </div>
            {filteredCompletedRewards.length > 0 && (
              <div className="text-sm font-semibold text-green-800 bg-green-100 px-3 py-1.5 rounded-lg border border-green-200">
                Total Paid: {formatCurrency(filteredCompletedRewards.reduce((sum, item) => sum + (item.rewardAmount || 0), 0))}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sponsor Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward / Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCompletedRewards.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      No distributed rewards found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredCompletedRewards.map((item, index) => (
                    <tr key={item._id || index} className="hover:bg-green-50/40 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{item.userName || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{item.userEmail || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{item.userPhone || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-800">
                        {item.mySponsorCode || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 text-xs font-bold text-white rounded-full ${getRankColor(item.rank)}`}>
                          {(item.rank || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-base font-bold text-green-600">
                          {formatCurrency(item.rewardAmount || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-800">
                          {formatCurrency(item.businessUsed || 0)}
                        </div>
                        <div className="text-xs text-gray-400">
                          Matching: {formatCurrency(item.matchingBusiness || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="font-medium text-gray-900">
                          {item.distributedAt ? new Date(item.distributedAt).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {item.distributedAt ? new Date(item.distributedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 text-xs font-bold text-green-800 bg-green-100 rounded-full border border-green-300 inline-flex items-center gap-1">
                          <span>✓</span>
                          <span>COMPLETED</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewUserRewards(item.user)}
                          className="text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Regular Users Table */
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Lifetime Rewards Users ({filteredUsers.length})</h2>
            <p className="text-sm text-gray-500">Showing qualified users and those who have completed lifetime rewards</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sponsor Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Direct Sponsees</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Power Leg</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rest of Legs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Matching Business</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available Matching & Carry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rewards Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user, index) => (
                  <tr key={user._id || index} className={`hover:bg-gray-50 ${user.completed ? 'bg-green-50/40' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{user.email || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{user.phone || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.mySponsorCode || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.isCompletedAll ? (
                        <span className="px-2 py-1 text-xs font-bold text-white rounded-full bg-green-600">
                          COMPLETED ALL
                        </span>
                      ) : user.hasPendingRewards ? (
                        <div>
                          <span className="px-2 py-1 text-xs font-bold text-white rounded-full bg-orange-500">
                            PENDING REWARDS
                          </span>
                          {user.totalRewardsDistributed > 0 && (
                            <div className="text-[10px] text-green-700 font-semibold mt-1">
                              ✓ {user.totalRewardsDistributed} Completed
                            </div>
                          )}
                        </div>
                      ) : (user.completed || user.hasCompletedRewards || user.totalRewardsDistributed > 0) ? (
                        <div>
                          <span className="px-2 py-1 text-xs font-bold text-white rounded-full bg-green-500">
                            COMPLETED ({user.totalRewardsDistributed} Paid)
                          </span>
                          {user.qualifiesForCurrentReward && (
                            <div className="text-[10px] text-blue-600 font-semibold mt-1">
                              Next rank qualified!
                            </div>
                          )}
                        </div>
                      ) : user.qualifiesForCurrentReward ? (
                        <span className="px-2 py-1 text-xs font-bold text-white rounded-full bg-blue-500">
                          QUALIFIED
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium text-white rounded-full bg-gray-500">
                          NOT QUALIFIED
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.directSponseesCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(user.legStructure?.powerLeg?.totalBusiness || 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user.legStructure?.powerLeg?.memberCount || 0} members
                      </div>
                      <div className="text-xs text-green-600 font-medium">
                        Strongest direct
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(user.legStructure?.supportLeg?.totalBusiness || 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user.legStructure?.supportLeg?.memberCount || 0} members
                      </div>
                      <div className="text-xs text-purple-600 font-medium">
                        Other directs
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-orange-600">
                        {formatCurrency(user.legStructure?.matchingBusiness || 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Total Matching
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-blue-600">
                        {formatCurrency(user.newMatchingIncomeForReward ?? user.availableMatchingIncome ?? 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Available Matching
                      </div>
                      {user.carriedForwardBalance > 0 && (
                        <div className="text-xs text-purple-700 font-semibold mt-1">
                          Carry: {formatCurrency(user.carriedForwardBalance)}
                        </div>
                      )}
                      {user.qualifiesForCurrentReward && (
                        <div className="text-xs text-green-600 font-medium mt-1">
                          ✓ Qualified
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getRankColor(user.currentRank || 'NONE')}`}>
                        {(user.currentRank || 'NONE').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.totalRewardsDistributed || 0} of 12
                      </div>
                      <div className="text-xs text-gray-500">
                        Different reward levels
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleCalculateBinaryTree(user._id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Calculate
                        </button>
                        {user.qualifiesForCurrentReward && user.currentRank && (
                          <button
                            onClick={() => handleDistributeRewardFromTable(user._id, user.currentRank)}
                            className="text-green-600 hover:text-green-900 font-bold"
                            title={`Send ${user.currentRank.replace(/_/g, ' ')} reward`}
                          >
                            Send Reward
                          </button>
                        )}
                        {user.hasPendingRewards && user.currentRank && (
                          <button
                            onClick={() => handleDistributeRewardFromTable(user._id, user.currentRank)}
                            className="text-orange-600 hover:text-orange-900 font-bold"
                            title={`Send pending ${user.currentRank.replace(/_/g, ' ')} reward`}
                          >
                            Send Reward
                          </button>
                        )}
                        <button
                          onClick={() => handleViewUserRewards(user)}
                          className="text-green-600 hover:text-green-900"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && userRewards && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedUser.name} - Lifetime Rewards</h2>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {/* Power Leg and Support Leg Structure */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2">Power Leg</h3>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(userRewards.powerLeg?.totalBusiness || 0)}
                </p>
                <p className="text-sm text-green-600">
                  {userRewards.powerLeg?.memberCount || 0} members
                </p>
                <p className="text-sm text-green-700 mt-2">
                  Strongest direct (highest total business - self + downline)
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-2">Rest of legs</h3>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(userRewards.supportLeg?.totalBusiness || 0)}
                </p>
                <p className="text-sm text-purple-600">
                  {userRewards.supportLeg?.memberCount || 0} members
                </p>
                <p className="text-sm text-purple-700 mt-2">
                  All other directs&apos; total business (self + downline) combined
                </p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-2">Total Matching Business</h3>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(userRewards.matchingBusiness || 0)}
                </p>
                <p className="text-sm text-orange-600">
                  Min(Power, Support) - Total
                </p>
                <p className="text-sm text-orange-700 mt-2">
                  Cumulative (for reference)
                </p>
              </div>
            </div>
            
            {/* Available Matching Income & Carry Forward */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">Available Matching Income for Current Reward</h3>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(userRewards.sequentialRewards?.businessForCurrentReward || 0)}
                  </p>
                  <p className="text-sm text-blue-600">
                    Eligible matching income for current rank qualification
                  </p>
                </div>
                {userRewards.sequentialRewards?.carriedForwardBalance > 0 && (
                  <div className="bg-purple-100 border border-purple-300 p-2.5 rounded-lg text-right">
                    <span className="text-xs font-semibold text-purple-700 block uppercase tracking-wide">Carry Forward to Next</span>
                    <span className="text-xl font-bold text-purple-800">
                      {formatCurrency(userRewards.sequentialRewards.carriedForwardBalance)}
                    </span>
                  </div>
                )}
              </div>
              {userRewards.sequentialRewards?.totalBusinessConsumed > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Total business consumed by previous distributed rewards: {formatCurrency(userRewards.sequentialRewards.totalBusinessConsumed)}
                </p>
              )}
              {userRewards.sequentialRewards?.currentRewardConfig && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progress to {userRewards.sequentialRewards.currentRewardRank.replace('_', ' ')} ({formatCurrency(userRewards.sequentialRewards.currentRewardConfig.matchingRequired)}):</span>
                    <span className="font-semibold">{((userRewards.sequentialRewards.businessForCurrentReward || 0) / userRewards.sequentialRewards.currentRewardConfig.matchingRequired * 100).toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((userRewards.sequentialRewards.businessForCurrentReward || 0) / userRewards.sequentialRewards.currentRewardConfig.matchingRequired) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Power Leg Members */}
            <div className="bg-white border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-3">Power leg (strongest direct)</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {userRewards.powerLeg?.users?.map((member, index) => (
                  <div key={member.userId?._id || member.userId} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium text-green-700">{member.name}</span>
                      <span className="text-gray-500 ml-2">({member.sponsorCode})</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-green-600">{formatCurrency(member.business)}</span>
                      <span className="text-xs bg-green-100 text-green-800 px-1 py-0.5 rounded ml-2">Power</span>
                    </div>
                  </div>
                )) || <p className="text-gray-500 text-sm">No power leg members</p>}
              </div>
            </div>

            {/* Support Leg Members */}
            <div className="bg-white border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-purple-800 mb-3">Rest of legs (other directs)</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {userRewards.supportLeg?.users?.slice(0, 10).map((member, index) => (
                  <div key={member.userId?._id || member.userId} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium text-purple-700">{member.name}</span>
                      <span className="text-gray-500 ml-2">({member.sponsorCode})</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-purple-600">{formatCurrency(member.business)}</span>
                      <span className="text-xs bg-purple-100 text-purple-800 px-1 py-0.5 rounded ml-2">Support</span>
                    </div>
                  </div>
                )) || <p className="text-gray-500 text-sm">No support leg members</p>}
                {userRewards.supportLeg?.users?.length > 10 && (
                  <p className="text-xs text-gray-500 text-center">...and {userRewards.supportLeg.users.length - 10} more members</p>
                )}
              </div>
            </div>

            {/* Sequential Reward Status */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg mb-6 border border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-3">Sequential Reward Status</h3>
              {userRewards.sequentialRewards ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Reward Index:</span>
                    <span className="font-medium">{userRewards.sequentialRewards.currentRewardIndex || 0} / {sequentialRewardOrder.length - 1}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Reward:</span>
                    <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getRankColor(userRewards.sequentialRewards.currentRewardRank || 'NONE')}`}>
                      {(userRewards.sequentialRewards.currentRewardRank || 'NONE').replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Available Matching Income for Current Reward:</span>
                    <span className="font-bold text-blue-600">{formatCurrency(userRewards.sequentialRewards.businessForCurrentReward || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Required for Current Reward:</span>
                    <span className="font-medium">{formatCurrency(userRewards.sequentialRewards.currentRewardConfig?.matchingRequired || 0)}</span>
                  </div>
                  {userRewards.sequentialRewards.carriedForwardBalance > 0 && (
                    <div className="flex justify-between bg-purple-50 p-1.5 rounded">
                      <span className="text-purple-800 font-medium">Carried Forward to Next Reward:</span>
                      <span className="font-bold text-purple-700">{formatCurrency(userRewards.sequentialRewards.carriedForwardBalance)}</span>
                    </div>
                  )}
                  {userRewards.sequentialRewards.totalBusinessConsumed > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Business Consumed by Completed Rewards:</span>
                      <span className="font-medium text-gray-700">{formatCurrency(userRewards.sequentialRewards.totalBusinessConsumed)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Progress to Current Reward:</span>
                    <span className="font-medium text-gray-700">
                      {((userRewards.sequentialRewards.businessForCurrentReward || 0) / (userRewards.sequentialRewards.currentRewardConfig?.matchingRequired || 1) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Rewards Distributed:</span>
                    <span className="font-bold text-green-600">{userRewards.sequentialRewards.totalRewardsDistributed || 0}</span>
                  </div>
                  {userRewards.sequentialRewards.nextRewardRank && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Next Eligible Reward:</span>
                      <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getRankColor(userRewards.sequentialRewards.nextRewardRank)}`}>
                        {userRewards.sequentialRewards.nextRewardRank.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {userRewards.sequentialRewards.lastRewardResetAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Reward Distribution / Reset:</span>
                      <span className="text-xs text-gray-500">{new Date(userRewards.sequentialRewards.lastRewardResetAt).toLocaleString()}</span>
                    </div>
                  )}
                  {userRewards.sequentialRewards.lastAchievementCreatedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Achievement Created At:</span>
                      <span className="text-xs text-gray-500">
                        {new Date(userRewards.sequentialRewards.lastAchievementCreatedAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs text-gray-600 italic">
                      * There are 12 different rewards (STAR, SILVER_STAR, GOLD, etc.). Each reward can only be received once.
                    </p>
                    <p className="text-xs text-gray-600 italic mt-1">
                      * Rewards must be distributed in strict sequential order one at a time. Only the current pending reward can be sent.
                    </p>
                    <p className="text-xs text-gray-600 italic mt-1">
                      * Any remaining eligible balance after reaching the current reward threshold is automatically carried forward toward the next Lifetime Reward.
                    </p>
                    {userRewards.sequentialRewards?.hasPendingRewards && (
                      <p className="text-xs text-amber-600 font-medium mt-2">
                        ⚠️ User has pending rewards awaiting distribution. No new rewards will be generated until pending rewards are distributed.
                      </p>
                    )}
                    {userRewards.sequentialRewards?.qualifiesForCurrentReward && !userRewards.sequentialRewards?.hasPendingRewards && (
                      <p className="text-xs text-green-600 font-medium mt-2">
                        🎉 User qualifies for current reward but achievement needs to be generated via calculation.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Sequential reward data not available</p>
              )}
            </div>

            {/* Current Rank */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Current Rank</h3>
              <span className={`px-3 py-1 text-lg font-medium text-white rounded-full ${getRankColor(userRewards.currentRank)}`}>
                {userRewards.currentRank || 'NONE'}
              </span>
            </div>

            {/* Sequential Rank Progress */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Sequential Reward Progress</h3>
              <div className="space-y-4">
                {sequentialRewardOrder.map((rank, index) => {
                  const config = rankConfigs[rank];
                  const achievement = userRewards.rankAchievements?.find(ach => ach.rank === rank);
                  
                  // Calculate progress based on sequential business
                  let progress = 0;
                  let currentBusiness = 0;
                  let requiredBusiness = config.matchingRequired;
                  
                  if (userRewards.sequentialRewards) {
                    const currentIndex = userRewards.sequentialRewards.currentRewardIndex || 0;
                    if (index === currentIndex) {
                      // This is the current reward being worked toward
                      currentBusiness = userRewards.sequentialRewards.businessForCurrentReward || 0;
                      progress = Math.min((currentBusiness / requiredBusiness) * 100, 100);
                    } else if (index < currentIndex) {
                      // Already completed
                      progress = 100;
                      currentBusiness = requiredBusiness;
                    } else {
                      // Future reward - not started yet
                      progress = 0;
                      currentBusiness = 0;
                    }
                  }

                  const isCurrentReward = userRewards.sequentialRewards?.currentRewardRank === rank;
                  const isCompleted = achievement?.rewardDistributed;
                  const isPending = achievement && !achievement.rewardDistributed;
                  const isLocked = index > (userRewards.sequentialRewards?.currentRewardIndex || 0);
                  
                  // Check if user qualifies for current reward (even without achievement)
                  const qualifiesForCurrent = userRewards.sequentialRewards?.qualifiesForCurrentReward && isCurrentReward;
                  
                  // Allow distribution if either:
                  // 1. There's a pending achievement for current reward, OR
                  // 2. User qualifies for current reward (achievement needs to be created)
                  const canDistribute = (isPending && isCurrentReward) || (qualifiesForCurrent && !isCompleted && !isPending);


                  return (
                    <div key={rank} className={`border rounded-lg p-4 ${isLocked ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${config.color}`}>
                            {rank.replace('_', ' ')}
                          </span>
                          <span className="text-sm text-gray-600">
                            Reward: {formatCurrency(config.reward)}
                          </span>
                          {isCurrentReward && (
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              Current
                            </span>
                          )}
                          {isCompleted && (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              Completed
                            </span>
                          )}
                          {isLocked && (
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                              Locked
                            </span>
                          )}
                        </div>
                        {canDistribute && (
                          <button
                            onClick={() => handleDistributeReward(selectedUser._id, rank)}
                            className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition-colors"
                            title={`Send ${formatCurrency(config.reward)} reward`}
                          >
                            Send Reward
                          </button>
                        )}
                        {isPending && !canDistribute && (
                          <span className="text-xs text-amber-600 ml-2">
                            (Complete current reward first)
                          </span>
                        )}
                      </div>

                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>
                            {isLocked ? 'Locked' : `Business: ${formatCurrency(currentBusiness)} / ${formatCurrency(requiredBusiness)}`}
                          </span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`${isCompleted ? 'bg-green-500' : isCurrentReward ? 'bg-blue-500' : 'bg-gray-400'} h-2 rounded-full`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>

                      {achievement && (
                        <div className="mt-2 text-sm text-gray-600">
                          Achieved: {new Date(achievement.achievedAt).toLocaleDateString()}
                          {achievement.businessUsed && (
                            <span className="text-blue-600 ml-2">
                              New Matching Income Used: {formatCurrency(achievement.businessUsed)}
                            </span>
                          )}
                          {achievement.matchingBusiness && (
                            <span className="text-orange-600 ml-2">
                              Total Matching: {formatCurrency(achievement.matchingBusiness)}
                            </span>
                          )}
                          {achievement.rewardDistributed && achievement.rewardDistributedAt && (
                            <span className="text-green-600 ml-2">
                              Distributed: {new Date(achievement.rewardDistributedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mt-4">
          {error}
        </div>
      )}
    </div>
  );
};

export default AdminLifetimeRewards;
